import PropTypes from 'prop-types'
import React from 'react'

import { generalStatus, types } from 'reduxModules/certificates'
import { generalCertificateDownloadUrl } from 'reduxModules/certificates/selectors'

import './styles.css'

/**
 * The All-Courses certificate (SPEC-040) at the top of My Certificates: one
 * portrait PDF listing every completed course, verifiable at a single link.
 * Shown only when the student has completed something (the page gates it).
 *
 * States follow the general slice: `none` offers to generate with the name
 * already on file (the backend resolves it; a missing name comes back as a
 * 400 the page turns into the name dialog), `success` shows the certificate,
 * `error` offers a retry and nothing else — a user who already has a
 * certificate must never be offered "Get" over it by mistake.
 *
 * The certificate is a snapshot. When newer completions exist, Download
 * brings it up to date first (silently, same code and link) and then opens
 * the fresh PDF — the person asked for their certificate, so they get the
 * current one; nobody is asked to "regenerate".
 */
function GeneralCertificatePanel({
  general,
  completionsCount,
  token = '',
  generating = false,
  onGenerate,
  onEditName,
  onRetry
}) {
  if (general.status === generalStatus.initial) return null

  const { status, data } = general
  const stale =
    status === generalStatus.success &&
    data &&
    completionsCount > data.courses.length

  // Reserve the tab inside the click (pop-up blockers allow that), refresh
  // the certificate, then point the tab at the download. On failure the
  // dispatcher has already toasted; just close the empty tab.
  const downloadLatest = (event) => {
    if (!stale || generating) return
    event.preventDefault()
    const tab = window.open('', '_blank')
    if (tab && tab.document)
      tab.document.title = 'Preparing your certificate…'
    onGenerate({ silent: true }).then((res) => {
      const url = generalCertificateDownloadUrl(token)
      if (res && res.type === types.CERTIFICATE_GENERAL_GENERATE_SUCCESS) {
        if (tab) tab.location.href = url
        else window.open(url, '_blank', 'noreferrer')
      } else if (tab) tab.close()
    })
  }

  return (
    <section
      className="general-certificate"
      aria-label="All-Courses Certificate"
    >
      <div className="general-certificate__header">
        <span className="general-certificate__seal" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path
              fill="currentColor"
              d="M12 2a5 5 0 0 0-3 9v0l-2 9 5-2.6L17 20l-2-9v0a5 5 0 0 0-3-9Zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"
            />
          </svg>
        </span>
        <div className="general-certificate__text">
          <p className="general-certificate__subtitle">
            One certificate listing every course you&apos;ve completed
          </p>
          {status === generalStatus.error && (
            <p className="general-certificate__note">
              We couldn&apos;t load your All-Courses Certificate.
            </p>
          )}
        </div>
      </div>

      <div
        className={`general-certificate__actions${
          status === generalStatus.none ? ' -fit' : ''
        }`}
      >
        {status === generalStatus.error && (
          <button
            type="button"
            className="btn -mini -border-white general-certificate__btn"
            onClick={onRetry}
          >
            Retry
          </button>
        )}

        {status === generalStatus.none && (
          <>
            <button
              type="button"
              className="btn -orange -mini general-certificate__btn -fit"
              onClick={() => onGenerate()}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Get All-Courses Certificate'}
            </button>
            <button
              type="button"
              className="general-certificate__link"
              onClick={onEditName}
              disabled={generating}
            >
              Use a different name
            </button>
          </>
        )}

        {status === generalStatus.success && data && (
          <>
            <a
              href={generalCertificateDownloadUrl(token)}
              target="_blank"
              rel="noreferrer"
              className="btn -orange -mini general-certificate__btn"
              onClick={downloadLatest}
              aria-disabled={generating || undefined}
            >
              {generating ? 'Preparing...' : 'Download PDF'}
            </a>
            <span className="general-certificate__issued">
              <span className="general-certificate__issued-label">Issued to</span>
              <button
                type="button"
                className="general-certificate__name"
                onClick={onEditName}
                title={data.printedName}
                disabled={generating}
                aria-label={`Edit the name on this certificate: ${data.printedName}`}
              >
                {data.printedName}
              </button>
            </span>
          </>
        )}
      </div>
    </section>
  )
}

GeneralCertificatePanel.propTypes = {
  general: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(generalStatus)).isRequired,
    data: PropTypes.shape({
      printedName: PropTypes.string,
      generatedAt: PropTypes.string,
      courses: PropTypes.arrayOf(PropTypes.object),
      verificationCode: PropTypes.string
    })
  }).isRequired,
  completionsCount: PropTypes.number.isRequired,
  token: PropTypes.string,
  generating: PropTypes.bool,
  onGenerate: PropTypes.func.isRequired,
  onEditName: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired
}

export default GeneralCertificatePanel
